import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export const DiscordSync = () => {
  const [discordUserId, setDiscordUserId] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleLinkDiscord = async () => {
    const trimmedId = discordUserId.trim();
    
    if (!trimmedId) {
      toast({
        title: "Error",
        description: "Please enter your Discord User ID",
        variant: "destructive",
      });
      return;
    }

    // Validate Discord ID format (17-19 digit numeric string)
    if (!/^\d{17,19}$/.test(trimmedId)) {
      toast({
        title: "Invalid Discord ID",
        description: "Discord User ID must be a 17-19 digit number. Find it in Discord Settings → Advanced → Developer Mode → Right-click your profile → Copy User ID",
        variant: "destructive",
      });
      return;
    }

    setIsLinking(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ discord_user_id: trimmedId })
        .eq('id', user?.id);

      if (error) throw error;

      toast({
        title: "Success!",
        description: "Discord account linked successfully",
      });
      setDiscordUserId("");
    } catch (error) {
      console.error('Error linking Discord:', error);
      toast({
        title: "Error",
        description: "Failed to link Discord account",
        variant: "destructive",
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleSyncRole = async () => {
    setIsSyncing(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) throw new Error("Not authenticated");

      const { data, error } = await supabase.functions.invoke('sync-discord-role', {
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: data.success ? "Success!" : "Info",
        description: data.message || data.error,
        variant: data.success ? "default" : "destructive",
      });
    } catch (error) {
      console.error('Error syncing Discord role:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to sync Discord role",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-background to-muted/30 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Discord Integration
          <span className="text-2xl">⚜️</span>
        </CardTitle>
        <CardDescription>
          Link your Discord account to receive the premium role
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="discord-id" className="text-sm font-medium">
            Discord User ID
          </label>
          <div className="flex gap-2">
            <Input
              id="discord-id"
              placeholder="Enter your Discord User ID"
              value={discordUserId}
              onChange={(e) => setDiscordUserId(e.target.value)}
              disabled={isLinking}
            />
            <Button 
              onClick={handleLinkDiscord} 
              disabled={isLinking}
              className="whitespace-nowrap"
            >
              {isLinking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Linking...
                </>
              ) : (
                "Link Account"
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            To find your Discord User ID: Enable Developer Mode in Discord Settings → Right-click your profile → Copy User ID
          </p>
        </div>

        <Button 
          onClick={handleSyncRole} 
          disabled={isSyncing}
          variant="outline"
          className="w-full"
        >
          {isSyncing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Syncing Role...
            </>
          ) : (
            "Sync Premium Role"
          )}
        </Button>
      </CardContent>
    </Card>
  );
};